<template>
  <div class="w-full">
    <form @submit.prevent="sendStageForm">
      <InputField
        id="initial_stage_name"
        v-model="initial_stage_name"
        type="text"
        left-icon-class="icon-users"
        right-icon-class="icon-close input__right-icon--hover"
        :label="$t('stage_title')"
        :has-error="
          v$.initial_stage_name.$invalid && v$.initial_stage_name.$anyDirty
        "
        :has-right-reset-button="true"
        @on-right-click="initial_stage_name = ''"
      />

      <InputField
        id="initial_stage_slug"
        ref="subdomainInput"
        v-model="initial_stage_slug"
        type="text"
        left-icon-class="icon-world-net"
        right-icon-class="icon-close input__right-icon--hover"
        :label="$t('initial_stage_slug')"
        :fixed-append-text="domain"
        :has-error="
          v$.initial_stage_slug.$invalid && v$.initial_stage_slug.$anyDirty
        "
        :has-right-reset-button="true"
        @on-right-click="initial_stage_slug = ''"
        @click="isSlugEdited = true"
      />

      <div
        v-if="
          initial_stage_slug && !v$.initial_stage_slug.validFullDomain.$invalid
        "
        class="mb-24"
      >
        <CardWrapper
          v-show="v$.initial_stage_slug.exists.$invalid"
          type-class="card--error"
          left-icon-class="icon-circle-close"
        >
          <template #body>
            {{ $t('domain_not_available', { domain: subdomainFull }) }}
          </template>
        </CardWrapper>
        <CardWrapper
          v-show="!v$.initial_stage_slug.exists.$invalid"
          type-class="card--success"
          left-icon-class="icon-circle-success"
        >
          <template #body>
            {{ $t('domain_available', { domain: subdomainFull }) }}
          </template>
        </CardWrapper>
      </div>
      <div
        v-else-if="
          initial_stage_slug && v$.initial_stage_slug.validFullDomain.$invalid
        "
        class="mb-24"
      >
        <CardWrapper
          type-class="card--error"
          left-icon-class="icon-circle-close"
        >
          <template #body>
            {{ $t('domain_not_valid', { domain: subdomainFull }) }}
          </template>
        </CardWrapper>
      </div>

      <button
        class="btn btn--purple"
        :class="{ 'btn--loading': loading }"
        :disabled="!isValidForm || loading"
        type="submit"
      >
        <template v-if="loading">
          <LoadingDots />
        </template>
        <template v-else>
          <div v-if="isOnboardingStageCreation" class="flex">
            {{ $t('continue') }}
            <span class="icon-simple-arrow-right btn__icon"></span>
          </div>
          <div v-else class="flex">
            {{ $t('create_stage') }}
          </div>
        </template>
      </button>
    </form>
  </div>
</template>

<script setup>
import { InputField } from '@snowshoe-frontend/form-fields-components'
import { CardWrapper, LoadingDots } from '@snowshoe-frontend/shared-components'

import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { ref, watch, computed, onMounted, onUpdated } from 'vue'
import isValidFullDomain from 'is-valid-domain'
import commonServices from '@/api/services/commonServices.js'
import { useCommonStore } from '@/stores/commonStore'

const props = defineProps({
  isCountrySelected: {
    type: Boolean,
    default: false
  },
  isOnboardingStageCreation: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['onSendStageData'])

const initial_stage_name = ref('')
const initial_stage_slug = ref('')
const domain = ref(import.meta.env.VITE_ACCOUNT_TLD)
const isValidSlugServiceRes = ref(false)
const isSlugEdited = ref(false)
const commonStore = useCommonStore()

const rules = computed(() => ({
  initial_stage_name: { required, $autoDirty: true },
  initial_stage_slug: {
    required,
    $autoDirty: true,
    validFullDomain: () => isFullDomainValid.value,
    exists: () => isValidSlugServiceRes.value
  }
}))

const v$ = useVuelidate(rules, { initial_stage_name, initial_stage_slug })

const subdomainFull = computed(
  () => `${initial_stage_slug.value}${domain.value}`
)

const isFullDomainValid = computed(() => {
  const fullStageURL = `${initial_stage_slug.value}${domain.value}`
  return isValidFullDomain(fullStageURL, { subdomain: true })
})

const isValidForm = computed(
  () => !v$.value.$invalid && props.isCountrySelected
)

const sendStageForm = () => {
  emit('onSendStageData', {
    initial_stage_name: initial_stage_name.value,
    initial_stage_slug: initial_stage_slug.value + domain.value
  })
}

const formatSlug = async (value) => {
  if (value && !isSlugEdited.value) {
    initial_stage_slug.value = value.trim().toLowerCase().split(' ').join('-')
  }
}

const validateSlug = async (slug) => {
  if (slug) {
    const res = await commonServices.validateSlug(encodeURIComponent(slug))
    isValidSlugServiceRes.value = res.data.status === 'ok'
  } else {
    isValidSlugServiceRes.value = false
  }
}

watch(initial_stage_name, (newValue) => {
  formatSlug(newValue)
})

watch(initial_stage_slug, (newValue) => {
  formatSlug(newValue)
  validateSlug(`${newValue}${domain.value}`)
})

onMounted(() => {
  if (!isValidForm.value) {
    commonStore.setDisableStep(commonStore.steps[1].routeName)
  }
})

onUpdated(() => {
  if (!isValidForm.value) {
    commonStore.setDisableStep(commonStore.steps[1].routeName)
  }
})
</script>
